import { useEffect, useRef } from "react";
import type { ZObject } from "../lib/protocol.ts";

export const Z_META: Record<ZObject["kind"], { icon: string; label: string }> = {
  text: { icon: "ri-text", label: "Text" },
  image: { icon: "ri-image-line", label: "Image" },
  fill: { icon: "ri-shape-2-line", label: "Shape" },
  stroke: { icon: "ri-shape-line", label: "Outline" },
  shade: { icon: "ri-contrast-drop-line", label: "Gradient" },
};

/** Objects panel: every drawing operation on the current page, listed
 *  in paint order (topmost first — PDF content is a flat display list,
 *  so paint order IS the z-index; only tagged PDFs carry a logical
 *  hierarchy and this doesn't reconstruct one). Selecting a row
 *  highlights it on the page and opens its compact properties. */
export function ObjectsPanel({
  pageIndex,
  pageCount,
  objects,
  selectedId,
  busy,
  onSelect,
  onEditText,
  onGoToPage,
  onClose,
  paneWidth,
}: {
  pageIndex: number;
  pageCount: number;
  objects: ZObject[] | null;
  selectedId: string | null;
  busy: boolean;
  onSelect: (o: ZObject | null) => void;
  onEditText: (o: ZObject) => void;
  onGoToPage: (p: number) => void;
  onClose: () => void;
  paneWidth: number;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Preview → panel: the page heading follows the viewer.
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [pageIndex]);

  const rows = objects; // shapes in paint order, then images, then text
  const selected = objects?.find((o) => o.id === selectedId) ?? null;
  const fieldCls =
    "h-7 w-full min-w-0 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-[11px]";

  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col bg-[color:var(--color-bg)]"
      style={{ width: paneWidth }}
    >
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] p-3">
        <i className="ri-stack-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Objects</h2>
        <span className="text-xs text-[color:var(--color-fg-muted)]">{rows?.length ?? "—"}</span>
        <button
          type="button"
          title="Close"
          aria-label="Close objects"
          onClick={onClose}
          className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-[color:var(--color-border)] px-2 py-1 text-[11px]">
        <button
          type="button"
          disabled={pageIndex === 0}
          onClick={() => onGoToPage(pageIndex - 1)}
          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
        >
          <i className="ri-arrow-left-s-line" aria-hidden />
        </button>
        <span className="font-medium">
          Page {pageIndex + 1} of {pageCount}
        </span>
        <button
          type="button"
          disabled={pageIndex >= pageCount - 1}
          onClick={() => onGoToPage(pageIndex + 1)}
          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
        >
          <i className="ri-arrow-right-s-line" aria-hidden />
        </button>
        <span className="ml-auto text-[10px] text-[color:var(--color-fg-muted)]">paint order</span>
      </div>

      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {!objects && (
          <p className="p-3 text-xs text-[color:var(--color-fg-muted)]">Reading page…</p>
        )}
        {rows?.length === 0 && (
          <p className="p-3 text-xs text-[color:var(--color-fg-muted)]">
            Nothing drawn on this page.
          </p>
        )}
        {rows?.map((o) => {
          const meta = Z_META[o.kind];
          const active = o.id === selectedId;
          return (
            <div key={o.id}>
              <button
                type="button"
                onClick={() => onSelect(active ? null : o)}
                className={`flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left ${
                  active
                    ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                    : "hover:bg-[color:var(--color-bg-subtle)]"
                }`}
              >
                <span className="w-6 shrink-0 text-right font-mono text-[9px] opacity-60">
                  {o.index}
                </span>
                <i className={`${meta.icon} shrink-0 text-xs`} aria-hidden />
                {o.color && (
                  <span
                    className="h-3 w-3 shrink-0 rounded-[2px] border border-black/20"
                    style={{ background: o.color }}
                  />
                )}
                <span className="min-w-0 flex-1 truncate text-[11px]">{o.label}</span>
              </button>
              {active && selected && (
                <div className="mb-1 space-y-1 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] p-1.5">
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[10px] text-[color:var(--color-fg-muted)]">
                    <span>Type</span>
                    <span className="text-[color:var(--color-fg)]">
                      {meta.label} · z {selected.index}
                    </span>
                    <span>Position</span>
                    <span className="text-[color:var(--color-fg)]">
                      {Math.round(selected.bbox[0])}, {Math.round(selected.bbox[1])} pt
                    </span>
                    <span>Size</span>
                    <span className="text-[color:var(--color-fg)]">
                      {Math.round(selected.bbox[2] - selected.bbox[0])} ×{" "}
                      {Math.round(selected.bbox[3] - selected.bbox[1])} pt
                    </span>
                    {selected.size !== undefined && (
                      <>
                        <span>Font size</span>
                        <span className="text-[color:var(--color-fg)]">{selected.size} pt</span>
                      </>
                    )}
                    {selected.alpha !== undefined && selected.alpha < 0.999 && (
                      <>
                        <span>Opacity</span>
                        <span className="text-[color:var(--color-fg)]">
                          {Math.round(selected.alpha * 100)}%
                        </span>
                      </>
                    )}
                  </div>
                  {selected.kind === "text" && (
                    <input
                      readOnly
                      value={selected.text ?? ""}
                      className={fieldCls}
                      title="Text content — use Edit to change it"
                    />
                  )}
                  <div className="flex items-center gap-1">
                    {selected.kind === "text" && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onEditText(selected)}
                        className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded border border-[color:var(--color-border)] text-[11px] hover:bg-[color:var(--color-bg)] disabled:opacity-40"
                      >
                        <i className="ri-edit-line" aria-hidden /> Edit text
                      </button>
                    )}
                    {selected.kind !== "text" && (
                      <p className="text-[10px] leading-snug text-[color:var(--color-fg-muted)]">
                        Selected on the page — use its on-page frame to recolour, move or delete.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="border-t border-[color:var(--color-border)] p-2 text-[10px] text-[color:var(--color-fg-muted)]">
        Shapes are listed in paint order (later = on top); text and images follow. PDF pages are
        a flat display list, so there's no object hierarchy unless the file is tagged.
      </p>
    </div>
  );
}
